import { Key, useEffect, useMemo, useState } from "react";
import { Form } from "antd";

import { TemplateAdministrationInstitutionVehicle, TemplateAdministrationInstitutionVehicleUpsert } from "../../Models";
import { IPlacementAgentsAdminProps } from "../../Components/Administration/IPlacementAgentsAdmin";

const formatThousands = (value?: number | string | null) => {
	if (value === null || value === undefined || value === "") {
		return "";
	}

	const numericValue = typeof value === "number"
		? value
		: Number(String(value).replace(/\./g, "").replace(",", "."));

	if (Number.isNaN(numericValue)) {
		return String(value);
	}

	return new Intl.NumberFormat("es-ES", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2
	}).format(numericValue);
};

const parseThousands = (value?: string) => {
	if (!value) {
		return 0;
	}

	return Number(value.replace(/\./g, "").replace(",", ".")) || 0;
};

const usePlacementAgentsAdmin = ({
	institutionVehicles,
	handleSaveInstitutionVehicle
}: Pick<IPlacementAgentsAdminProps, "institutionVehicles" | "handleSaveInstitutionVehicle">) => {
	const [selectedInstitutionId, setSelectedInstitutionId] = useState<number | null>(null);
	const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);
	const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
	const [drafts, setDrafts] = useState<Record<string, TemplateAdministrationInstitutionVehicleUpsert>>({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [form] = Form.useForm<TemplateAdministrationInstitutionVehicleUpsert>();

	useEffect(() => {
		const nextDrafts = institutionVehicles.reduce<Record<string, TemplateAdministrationInstitutionVehicleUpsert>>((accumulator, row) => {
			const suitabilityTestType = typeof row.suitabilityTestType === "number" ? row.suitabilityTestType : 0;

			accumulator[`${row.idInstitution}_${row.idVehicle}`] = {
				idInstitution: row.idInstitution,
				idVehicle: row.idVehicle,
				active: row.active,
				afin: row.afin,
				maxAvailable: row.maxAvailable,
				includesSuitabilityTest: suitabilityTestType > 0 ? true : row.includesSuitabilityTest,
				suitabilityTestType,
				includesInvestorStatus: row.includesInvestorStatus,
				includesRepresentativesPF: row.includesRepresentativesPF,
				includesInvestorWithoutNIF: row.includesInvestorWithoutNIF,
				includesCRS: row.includesCRS,
				includesFATCA: row.includesFATCA,
				includesBeneficiariesGreater5: row.includesBeneficiariesGreater5,
				includesRegistrationNumber: row.includesRegistrationNumber
			};
			return accumulator;
		}, {});

		setDrafts(nextDrafts);
	}, [institutionVehicles]);

	const filteredInstitutionVehicles = useMemo(() => (
		institutionVehicles.filter((row) =>
			(!selectedInstitutionId || row.idInstitution === selectedInstitutionId)
			&& (!selectedVehicleId || row.idVehicle === selectedVehicleId)
		)
	), [institutionVehicles, selectedInstitutionId, selectedVehicleId]);

	const groupedInstitutionVehicles = useMemo(() => {
		const groups = filteredInstitutionVehicles.reduce<Record<string, {
			idInstitution: number;
			institutionName: string;
			rows: TemplateAdministrationInstitutionVehicle[];
		}>>((accumulator, row) => {
			const key = row.idInstitution.toString();
			if (!accumulator[key]) {
				accumulator[key] = {
					idInstitution: row.idInstitution,
					institutionName: row.institutionName,
					rows: []
				};
			}

			accumulator[key].rows.push(row);
			return accumulator;
		}, {});

		return Object.values(groups)
			.sort((a, b) => a.institutionName.localeCompare(b.institutionName))
			.map((group) => ({
				...group,
				rows: group.rows.sort((a, b) => a.vehicleName.localeCompare(b.vehicleName))
			}));
	}, [filteredInstitutionVehicles]);

	const filteredExpandedRowKeys = useMemo(() => (
		expandedRowKeys.filter(key =>
			filteredInstitutionVehicles.some(row => `${row.idInstitution}_${row.idVehicle}` === key)
		)
	), [expandedRowKeys, filteredInstitutionVehicles]);

	const updateDraft = (
		idInstitution: number,
		idVehicle: number,
		field: keyof TemplateAdministrationInstitutionVehicleUpsert,
		value: boolean | number
	) => {
		const draftKey = `${idInstitution}_${idVehicle}`;
		setDrafts((currentState) => ({
			...currentState,
			[draftKey]: {
				...currentState[draftKey],
				[field]: value
			}
		}));
	};

	const openModal = () => {
		form.setFieldsValue({
			idInstitution: selectedInstitutionId ?? undefined,
			idVehicle: selectedVehicleId ?? undefined,
			active: true,
			afin: false,
			maxAvailable: 0,
			includesSuitabilityTest: false,
			suitabilityTestType: 0,
			includesInvestorStatus: false,
			includesRepresentativesPF: false,
			includesInvestorWithoutNIF: false,
			includesCRS: false,
			includesFATCA: false,
			includesBeneficiariesGreater5: false,
			includesRegistrationNumber: false
		});
		setIsModalOpen(true);
	};

	const handleCreate = async () => {
		const values = await form.validateFields();
		await handleSaveInstitutionVehicle(values);
		form.resetFields();
		setIsModalOpen(false);
	};

	return {
		selectedInstitutionId,
		setSelectedInstitutionId,
		selectedVehicleId,
		setSelectedVehicleId,
		expandedRowKeys,
		setExpandedRowKeys,
		drafts,
		isModalOpen,
		setIsModalOpen,
		form,
		groupedInstitutionVehicles,
		filteredExpandedRowKeys,
		updateDraft,
		openModal,
		handleCreate,
		formatThousands,
		parseThousands
	};
};

export default usePlacementAgentsAdmin;
